import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, User, DollarSign, TrendingUp, TrendingDown, Users, Activity } from 'lucide-react';
import api from '../../helpers/api_helper';

interface AgentCommissionData {
  success: boolean;
  agent: {
    id: number;
    mobile_number: string;
    name: string;
    app_id: string;
    commission_percentage: number;
  };
  period: {
    date_from: string | null;
    date_to: string | null;
  };
  message: string;
  user_count: number;
  metrics: {
    user_count: number;
    total_stake: number;
    lost_stake: number;
    payout_total: number;
    house_profit: number;
  };
  commission_due: number;
  activity: {
    total_users: number;
    active_users: number;
    total_deposits: number;
    total_withdrawals: number;
    total_bets: number;
    won_bets: number;
    lost_bets: number;
    pending_bets: number;
  };
}

const AgentCommissionSummary: React.FC = () => {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AgentCommissionData | null>(null);

  useEffect(() => {
    const fetchCommissionData = async () => {
      if (!agentId) {
        setError('Agent ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/admin/agents/${agentId}/commission-summary/`);
        setData(response.data);
      } catch (err: any) {
        console.error('Error fetching agent commission data:', err);
        setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to fetch agent commission data');
      } finally {
        setLoading(false);
      }
    };

    fetchCommissionData();
  }, [agentId]);

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!agentId) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Agent Commission Summary</h1>
            <button 
              onClick={() => navigate('/role-creation')} 
              className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Agent ID is missing. Please go back and try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-xl shadow-sm border mb-6">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h1 className="text-2xl font-bold text-gray-900">Agent Commission Summary</h1>
          <button 
            onClick={() => navigate('/role-creation')} 
            className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
            <span className="text-gray-600">Loading commission data...</span>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium mb-2">Error loading commission data</p>
              <p className="text-red-600 text-sm">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        ) : data ? (
          <div className="p-6 space-y-6">
            {/* Agent Information */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Agent Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Agent Name</p>
                  <p className="text-lg font-bold text-gray-900">{data.agent.name}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Mobile Number</p>
                  <p className="text-lg font-bold text-gray-900">{data.agent.mobile_number}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Commission Percentage</p>
                  <p className="text-lg font-bold text-blue-600">{data.agent.commission_percentage.toFixed(2)}%</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Agent ID</p>
                  <p className="text-lg font-bold text-gray-900">#{data.agent.id}</p>
                </div>
              </div>
            </div>

            {/* Period Information */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Period</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">From:</span>
                  <span className="ml-2 font-medium text-gray-900">{formatDate(data.period.date_from)}</span>
                </div>
                <div>
                  <span className="text-gray-600">To:</span>
                  <span className="ml-2 font-medium text-gray-900">{formatDate(data.period.date_to)}</span>
                </div>
              </div>
            </div>

            {/* Commission Due - Highlighted */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Commission Due</p>
                  <p className="text-3xl font-bold text-green-700">{formatCurrency(data.commission_due)}</p>
                </div>
                <DollarSign className="w-12 h-12 text-green-600 opacity-50" />
              </div>
            </div>

            {/* Metrics Cards */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Financial Metrics
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-orange-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Total Stake</p>
                  <p className="text-xl font-bold text-blue-900">{formatCurrency(data.metrics.total_stake)}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <p className="text-xs font-medium text-red-600 uppercase tracking-wide mb-1">Lost Stake</p>
                  <p className="text-xl font-bold text-red-900">{formatCurrency(data.metrics.lost_stake)}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 border border-purple-200">
                  <p className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-1">Payout Total</p>
                  <p className="text-xl font-bold text-purple-900">{formatCurrency(data.metrics.payout_total)}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                  <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide mb-1">House Profit</p>
                  <p className="text-xl font-bold text-indigo-900">{formatCurrency(data.metrics.house_profit)}</p>
                </div>
                <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                  <p className="text-xs font-medium text-cyan-600 uppercase tracking-wide mb-1">User Count</p>
                  <p className="text-xl font-bold text-cyan-900">{data.metrics.user_count}</p>
                </div>
              </div>
            </div>

            {/* Activity Metrics */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Activity Metrics
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{data.activity.total_users}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Active Users</p>
                  <p className="text-2xl font-bold text-green-900">{data.activity.active_users}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Total Deposits</p>
                  <p className="text-2xl font-bold text-blue-900">{formatCurrency(data.activity.total_deposits)}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <p className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-1">Total Withdrawals</p>
                  <p className="text-2xl font-bold text-orange-900">{formatCurrency(data.activity.total_withdrawals)}</p>
                </div>
              </div>
            </div>

            {/* Betting Activity */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Betting Activity
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                  <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide mb-1">Total Bets</p>
                  <p className="text-2xl font-bold text-indigo-900">{data.activity.total_bets}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Won Bets</p>
                  <p className="text-2xl font-bold text-green-900">{data.activity.won_bets}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <p className="text-xs font-medium text-red-600 uppercase tracking-wide mb-1">Lost Bets</p>
                  <p className="text-2xl font-bold text-red-900">{data.activity.lost_bets}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <p className="text-xs font-medium text-yellow-600 uppercase tracking-wide mb-1">Pending Bets</p>
                  <p className="text-2xl font-bold text-yellow-900">{data.activity.pending_bets}</p>
                </div>
              </div>
            </div>

            {/* Message */}
            {data.message && (
              <div className="bg-orange-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">{data.message}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="p-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-600">No data available. Please try again.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentCommissionSummary;

